/**
 * The change-feed section of the Cloud board (L1): a digest of the newest 10
 * changes as a `Timeline` (a sequence of events is a timeline, not a table —
 * dashboard design rules §2), with the full attributed list one click deeper
 * at `/cloud/changes`.
 */

import { EmptyState, Timeline, Typography } from "@norican/react";

import type { BoardRange } from "@/components/board";
import type { CloudChanges } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";
import { DrillLink } from "@/lib/drill";

import { PanelCard } from "./panel-card";
import { CHANGE_TYPE_PARAM, CLOUD_CHANGES_PATH, SUB_PARAM } from "./params";
import { formatTimestamp, shortType } from "./status";

/** How many changes the L1 digest shows — the rest live at L2. */
const DIGEST_LIMIT = 10;

/** `StatusTone` → `TimelineTone` (the timeline has no `neutral`, it has `default`). */
const TIMELINE_TONE: Record<string, "success" | "danger" | "default"> = {
  Create: "success",
  Delete: "danger",
};

interface ChangesSectionProps {
  changes: CloudChanges;
  /** Selected subscription IDs from the board FilterRow ([] = all). */
  subIds: string[];
  /** The board's date window — narrows the digest when tighter than 48 h. */
  range: BoardRange;
}

export function ChangesSection({ changes, subIds, range }: ChangesSectionProps) {
  if (!changes.configured) {
    return (
      <section id="cloud-changes" className="scroll-mt-6">
        <PanelCard title="Change feed" caption="Not available">
          <Typography variant="body-sm" tone="muted">
            {changes.status_detail}
          </Typography>
        </PanelCard>
      </section>
    );
  }

  const windowEnd = range.toDate.getTime() + 86_400_000;
  const scoped = subIds.length > 0;
  const filtered = changes.changes.filter((change) => {
    if (scoped && !subIds.includes(change.subscription_id)) {
      return false;
    }
    if (!change.changed_at) {
      return true;
    }
    const at = new Date(change.changed_at).getTime();
    return at >= range.fromDate.getTime() && at < windowEnd;
  });

  const deletes = filtered.filter((change) => change.change_type === "Delete").length;
  const digest = filtered.slice(0, DIGEST_LIMIT);

  const caption = scoped
    ? `${formatCount(filtered.length)} listed changes in the selected subscriptions · ${formatCount(deletes)} deletes`
    : `${formatCount(changes.total)} changes in ${changes.window_hours} hours · ${formatCount(
        changes.total_last_24h,
      )} in the last 24`;

  return (
    <section id="cloud-changes" className="scroll-mt-6">
      <PanelCard
        title="Change feed"
        caption={
          <span className="flex flex-wrap items-center gap-3">
            <span>{caption}</span>
            <DrillLink
              to={CLOUD_CHANGES_PATH}
              params={scoped ? { [SUB_PARAM]: subIds } : {}}
              className="text-fg text-sm font-medium hover:underline"
            >
              View all {formatCount(changes.total)} →
            </DrillLink>
          </span>
        }
      >
        <div className="flex flex-col gap-4">
          {digest.length === 0 ? (
            <EmptyState
              size="sm"
              title="No resource changes in this window"
              description="Azure recorded no creates, updates, or deletes matching the filters."
            />
          ) : (
            <Timeline
              items={digest.map((change) => ({
                id: change.id,
                title: (
                  <DrillLink
                    to={CLOUD_CHANGES_PATH}
                    params={{ [CHANGE_TYPE_PARAM]: change.change_type }}
                    className="hover:underline"
                  >
                    {change.change_type}: {change.resource_name}
                  </DrillLink>
                ),
                timestamp: formatTimestamp(change.changed_at),
                tone: TIMELINE_TONE[change.change_type] ?? "default",
                description: [
                  change.changed_by ?? "unattributed",
                  change.resource_type ? shortType(change.resource_type) : null,
                  change.subscription_name,
                ]
                  .filter(Boolean)
                  .join(" · "),
              }))}
            />
          )}

          {changes.truncated && (
            <Typography variant="body-sm" tone="muted">
              The backend lists the newest {changes.changes.length} changes — the counts above cover
              the whole window.
            </Typography>
          )}

          {changes.errors.length > 0 && (
            <Typography variant="body-sm" tone="muted">
              Collection problems: {changes.errors.join(" · ")}
            </Typography>
          )}
        </div>
      </PanelCard>
    </section>
  );
}
